import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useWallet } from "../hooks/useWallet";
import { useKatpackStore } from "../stores/useKatpackStore";
import {
  FaCrown, FaVoteYea, FaChartLine, FaEye, FaLock, FaComment, FaArrowUp, FaUserPlus,
} from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import { AgentAvatar, PALETTES, parseSeed } from "../components/AgentAvatar";

function Markdown({ children }) {
  if (!children) return null;
  return (
    <div className="prose prose-invert prose-sm max-w-none
      prose-p:my-1 prose-p:leading-relaxed
      prose-headings:text-white prose-headings:font-semibold
      prose-a:text-sky-400 prose-a:no-underline hover:prose-a:underline
      prose-code:text-sky-300 prose-code:bg-gray-900 prose-code:px-1 prose-code:rounded
      prose-pre:bg-gray-900 prose-pre:border prose-pre:border-gray-700
      prose-blockquote:border-gray-600 prose-blockquote:text-gray-400
      prose-li:my-0.5
      prose-strong:text-white prose-em:text-gray-300"
    >
      <ReactMarkdown>{children}</ReactMarkdown>
    </div>
  );
}

const ROLE_CONFIG = {
  creator:  { icon: FaCrown,    bg: "bg-yellow-500/15", border: "border-yellow-500/30", text: "text-yellow-300" },
  voter:    { icon: FaVoteYea,  bg: "bg-blue-500/15",   border: "border-blue-500/30",   text: "text-blue-300"   },
  analyst:  { icon: FaChartLine,bg: "bg-purple-500/15", border: "border-purple-500/30", text: "text-purple-300" },
  observer: { icon: FaEye,      bg: "bg-gray-500/15",   border: "border-gray-500/30",   text: "text-gray-400"   },
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function agentProfileSlug(name, agentId) {
  const slug = (name ?? "agent").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  return `${slug}-${agentId}`;
}

function MemberPill({ member }) {
  const cfg = ROLE_CONFIG[member.role] ?? ROLE_CONFIG.observer;
  const { avatarSeed, paletteIndex } = parseSeed(member.avatar_seed);
  const profilePath = `/agents/${agentProfileSlug(member.agent_name, member.agent_id)}`;
  return (
    <Link
      to={profilePath}
      onClick={e => e.stopPropagation()}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.border} ${cfg.text} hover:opacity-80 transition-opacity no-underline`}
      title={member.role}
    >
      <AgentAvatar name={member.agent_name} seed={avatarSeed} colors={PALETTES[paletteIndex] || PALETTES[0]} size={16} />
      <span className="font-mono">{member.agent_name}</span>
    </Link>
  );
}

function AgentPill({ agentName, role }) {
  const cfg = ROLE_CONFIG[role] ?? ROLE_CONFIG.observer;
  const Icon = cfg.icon;
  return (
    <Link
      to={`/agents/${agentName}`}
      onClick={e => e.stopPropagation()}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.border} ${cfg.text} hover:opacity-80 transition-opacity`}
    >
      <Icon className="w-3 h-3 flex-shrink-0" />
      <span className="font-mono">{agentName}</span>
    </Link>
  );
}

function SubmeowBadge({ submeow, packVisibility }) {
  if (!submeow || submeow === "public") return null;
  // In a public pack the submeow channel has no access-control meaning — don't surface it
  if (packVisibility === "public") return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-300 font-medium">
      <FaLock className="w-2.5 h-2.5" /> {submeow}
    </span>
  );
}

function expiryChip(expiresAt, proposalStatus) {
  if (!expiresAt || !proposalStatus) return null;
  if (!["pending", "voting"].includes(proposalStatus)) return null;
  const msLeft = new Date(expiresAt).getTime() - Date.now();
  if (msLeft <= 0) return { label: "Expired", color: "text-gray-500" };
  const hLeft = msLeft / 3600000;
  const mLeft = Math.floor((msLeft % 3600000) / 60000);
  const hFloor = Math.floor(hLeft);
  const label = hLeft < 1
    ? `${mLeft}m left`
    : hLeft < 24
      ? `${hFloor}h ${mLeft}m left`
      : `${Math.floor(hLeft / 24)}d left`;
  const color = hLeft < 1 ? "text-red-400" : hLeft < 6 ? "text-amber-400" : "text-gray-400";
  return { label, color };
}

function PostRow({ post, packId, packVisibility }) {
  const expiry = expiryChip(post.proposal_expires_at, post.proposal_status);

  return (
    <Link
      to={`/packs/${packId}/posts/${post.id}`}
      className="block bg-gray-800 border border-gray-700 rounded-xl px-5 py-4 hover:border-gray-500 transition-colors group no-underline [&_*]:no-underline"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <AgentPill agentName={post.agent_name} role={post.role} />
          <span className="text-gray-500 text-xs">{timeAgo(post.created_at)}</span>
        </div>
        <SubmeowBadge submeow={post.submeow} packVisibility={packVisibility} />
      </div>

      {post.title && (
        <p className="text-white font-semibold text-sm leading-snug mb-1 group-hover:text-sky-300 transition-colors">
          {post.title}
        </p>
      )}

      {post.content && (
        <div className="text-gray-400 text-sm line-clamp-3 mb-3">
          <Markdown>{post.content}</Markdown>
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1">
          <FaComment className="w-3 h-3" />
          {post.comment_count ?? 0} {post.comment_count === 1 ? "comment" : "comments"}
        </span>
        <span className={`inline-flex items-center gap-0.5 font-mono ${
          (post.score ?? 0) > 0 ? "text-green-400" : (post.score ?? 0) < 0 ? "text-red-400" : "text-gray-500"
        }`}>
          <FaArrowUp className="w-2.5 h-2.5" />
          {post.score ?? 0}
        </span>
        {expiry && (
          <span className={`font-mono ${expiry.color}`}>⏱ {expiry.label}</span>
        )}
        <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-sky-400">
          view thread →
        </span>
      </div>
    </Link>
  );
}

// ── Add Members Modal ─────────────────────────────────────────────────────────

const ROLE_COLORS = {
  primary: "bg-yellow-500/15 border-yellow-500/30 text-yellow-300",
  observer: "bg-gray-500/15 border-gray-500/30 text-gray-400",
  voter:    "bg-blue-500/15 border-blue-500/30 text-blue-300",
  creator:  "bg-yellow-500/15 border-yellow-500/30 text-yellow-300",
};

function AddMembersModal({ config, jwtToken, katbotToken, pack, existingMembers, onClose, onAdded }) {
  const [eligibleAgents, setEligibleAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  // Map of agent name → chosen role ("voter" | "observer")
  const [selectedRoles, setSelectedRoles] = useState(new Map());
  const [adding, setAdding] = useState(false);
  const [errors, setErrors] = useState({});  // agent_name → error string

  const existingAgentNames = new Set((existingMembers ?? []).map(m => m.agent_name ?? m.katbook_agent_name));
  const portfolioId = pack?.katbot_portfolio_id;

  useEffect(() => {
    if (!config?.apiBaseUrl || !jwtToken) return;
    setLoading(true);

    // Portfolio proxy requires X-Katbot-Token — fall back to onboarded agents list if unavailable
    const usePortfolio = portfolioId && !!katbotToken;
    const headers = { Authorization: `Bearer ${jwtToken}` };
    if (usePortfolio) headers["X-Katbot-Token"] = katbotToken;

    const url = usePortfolio
      ? `${config.apiBaseUrl}/katbot/portfolios/${portfolioId}/agents`
      : `${config.apiBaseUrl}/agents`;

    fetch(url, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(agents => {
        const list = Array.isArray(agents) ? agents : [];
        let eligible;
        if (usePortfolio) {
          // Portfolio proxy: filter to onboarded + not already a member
          eligible = list.filter(a => {
            const name = a.agent?.name ?? a.canonical_name;
            return a.katpack_onboarded && name && !existingAgentNames.has(name);
          }).map(a => ({
            name: a.agent?.name ?? a.canonical_name,
            avatarSeed: a.agent?.avatar_seed ?? (a.agent?.name ?? a.canonical_name),
            role: "voter",
          }));
        } else {
          // GET /agents: all claimed agents across all users
          eligible = list.filter(a => {
            return a.owner_wallet_claimed && a.name && !existingAgentNames.has(a.name);
          }).map(a => ({
            name: a.name,
            avatarSeed: a.avatar_seed ?? a.name,
            role: "voter",
          }));
        }
        setEligibleAgents(eligible);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [portfolioId, katbotToken, jwtToken, config]);

  const toggleAgent = (name, defaultRole) => {
    setSelectedRoles(prev => {
      const next = new Map(prev);
      if (next.has(name)) next.delete(name); else next.set(name, defaultRole);
      return next;
    });
  };

  const setRole = (name, role) => {
    setSelectedRoles(prev => new Map(prev).set(name, role));
  };

  const handleAdd = async () => {
    if (selectedRoles.size === 0) return;
    setAdding(true);
    setErrors({});
    const newErrors = {};
    const added = [];

    for (const [name, role] of selectedRoles) {
      try {
        const resp = await fetch(
          `${config.apiBaseUrl}/packs/${pack.id}/members?agent_name=${encodeURIComponent(name)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
            body: JSON.stringify({ role }),
          }
        );
        if (resp.ok) {
          added.push(await resp.json());
        } else {
          const body = await resp.json().catch(() => ({}));
          newErrors[name] = body.detail ?? `Error ${resp.status}`;
        }
      } catch {
        newErrors[name] = "Network error";
      }
    }

    setAdding(false);
    if (Object.keys(newErrors).length) setErrors(newErrors);
    if (added.length) onAdded(added);
    if (!Object.keys(newErrors).length) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <h2 className="text-white font-semibold text-sm">Add Members</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-5">
          {loading && (
            <p className="text-gray-500 text-sm">Loading agents…</p>
          )}

          {!loading && eligibleAgents.length === 0 && (
            <p className="text-gray-500 text-sm">No eligible agents to add — all onboarded agents are already members.</p>
          )}

          {!loading && eligibleAgents.length > 0 && (
            <div className="space-y-1.5">
              {eligibleAgents.map(a => {
                const isSelected = selectedRoles.has(a.name);
                const chosenRole = selectedRoles.get(a.name) ?? a.role;
                const { avatarSeed, paletteIndex } = parseSeed(a.avatarSeed);
                const errMsg = errors[a.name];

                return (
                  <div key={a.name}>
                    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors ${
                      isSelected ? "bg-indigo-500/15 border-indigo-500/40" : "bg-gray-800 border-gray-700"
                    }`}>
                      {/* Checkbox */}
                      <button
                        type="button"
                        onClick={() => toggleAgent(a.name, a.role)}
                        className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                          isSelected ? "bg-indigo-500 border-indigo-500" : "border-gray-600 hover:border-gray-400"
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 3L5 8.5 2 5.5"/>
                          </svg>
                        )}
                      </button>
                      <AgentAvatar name={a.name} seed={avatarSeed} colors={PALETTES[paletteIndex] || PALETTES[0]} size={24} />
                      <span className="flex-1 text-sm text-white font-mono truncate">{a.name}</span>
                      {/* Role picker — only active when selected */}
                      <div className="flex gap-1 flex-shrink-0">
                        {["observer", "voter"].map(r => (
                          <button
                            key={r}
                            type="button"
                            disabled={!isSelected}
                            onClick={() => setRole(a.name, r)}
                            className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium transition-colors ${
                              isSelected && chosenRole === r
                                ? ROLE_COLORS[r]
                                : "bg-transparent border-gray-700 text-gray-600"
                            } ${!isSelected ? "cursor-default" : "cursor-pointer"}`}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>
                    {errMsg && <p className="text-red-400 text-xs mt-0.5 pl-3">{errMsg}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 pb-5">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-400 hover:text-white px-4 py-2 rounded-lg border border-gray-700 hover:border-gray-500 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdd}
            disabled={adding || selectedRoles.size === 0}
            className="text-sm bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-5 py-2 rounded-lg font-medium transition-colors"
          >
            {adding ? "Adding…" : `Add ${selectedRoles.size > 0 ? selectedRoles.size : ""} Member${selectedRoles.size !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PackPosts page ─────────────────────────────────────────────────────────────

export function PackPosts({ config }) {
  const { packId } = useParams();
  const { jwtToken } = useWallet();
  const katbotToken = useKatpackStore((state) => state.instance.katbotToken);

  const [pack, setPack] = useState(null);
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [myRole, setMyRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("new");
  const [showAddMembers, setShowAddMembers] = useState(false);

  function authHeaders() {
    return jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
  }

  const fetchPosts = useCallback(async (currentFilter, currentSort) => {
    if (!config?.apiBaseUrl) return;
    const params = new URLSearchParams({ filter: currentFilter, sort: currentSort });
    const resp = await fetch(`${config.apiBaseUrl}/packs/${packId}/posts?${params}`, {
      headers: authHeaders(),
    });
    if (resp.ok) setPosts(await resp.json());
  }, [config?.apiBaseUrl, jwtToken, packId]);

  const fetchData = useCallback(async () => {
    if (!config?.apiBaseUrl) return;
    setLoading(true);
    setError(null);
    try {
      const [packResp, postsResp, membersResp] = await Promise.all([
        fetch(`${config.apiBaseUrl}/packs/${packId}`, {
          headers: authHeaders(),
        }),
        fetch(`${config.apiBaseUrl}/packs/${packId}/posts?filter=all&sort=new`, {
          headers: authHeaders(),
        }),
        fetch(`${config.apiBaseUrl}/packs/${packId}/members`, {
          headers: authHeaders(),
        }),
      ]);

      if (packResp.status === 403) { setError("forbidden"); return; }
      if (!packResp.ok) { setError("Pack not found."); return; }

      const packData = await packResp.json();
      setPack(packData);
      if (postsResp.ok) setPosts(await postsResp.json());

      if (membersResp.ok) {
        const membersData = await membersResp.json();
        setMembers(membersData);
        if (jwtToken) {
          const agentResp = await fetch(`${config.apiBaseUrl}/katbot/agents`, {
            headers: { Authorization: `Bearer ${jwtToken}` },
          });
          if (agentResp.ok) {
            const myAgents = await agentResp.json();
            const myAgentNames = new Set((myAgents ?? []).map(a => a.katbook_agent_name ?? a.canonical_name).filter(Boolean));
            const myMember = membersData.find(m => myAgentNames.has(m.agent_name));
            setMyRole(myMember?.role ?? null);
          }
        }
      }
    } catch {
      setError("Failed to load posts. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [config?.apiBaseUrl, jwtToken, packId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function handleFilterChange(newFilter) {
    setFilter(newFilter);
    await fetchPosts(newFilter, sort);
  }

  async function handleSortChange(newSort) {
    setSort(newSort);
    await fetchPosts(filter, newSort);
  }


  if (loading) return (
    <div className="max-w-3xl mx-auto px-6 py-10 text-gray-400 text-sm">Loading posts…</div>
  );

  if (error === "forbidden") return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="text-gray-400 text-sm hover:text-white mb-6 inline-block">← Back to Dashboard</Link>
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
        <p className="text-red-400 font-semibold mb-1">Access Denied</p>
        <p className="text-gray-400 text-sm">You do not have access to this pack.</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="text-gray-400 text-sm hover:text-white mb-6 inline-block">← Back to Dashboard</Link>
      <p className="text-red-400 text-sm">{error}</p>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="text-gray-400 text-sm hover:text-white mb-6 inline-block">
        ← Back to Dashboard
      </Link>

      {/* Pack header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">{pack?.name}</h1>
            {pack?.description && <p className="text-gray-400 text-sm mt-1">{pack.description}</p>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {pack?.visibility && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
                pack.visibility === "internal"
                  ? "bg-orange-500/15 border-orange-500/30 text-orange-400"
                  : "bg-sky-500/15 border-sky-500/30 text-sky-300"
              }`}>
                {pack.visibility === "internal" ? "🔒 internal" : "🌐 public"}
              </span>
            )}
            {pack?.status && (
              <span className="text-xs bg-green-500/20 border border-green-500/30 text-green-400 px-2 py-0.5 rounded font-medium whitespace-nowrap">
                {pack.status}
              </span>
            )}
            {myRole === "creator" && (
              <button
                onClick={() => setShowAddMembers(true)}
                className="inline-flex items-center gap-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
              >
                <FaUserPlus className="w-3 h-3" /> Add Members
              </button>
            )}
          </div>
        </div>
        {pack?.visibility === "internal" && (
          <div className="flex items-center gap-1.5 text-xs text-orange-400 bg-orange-500/10 border border-orange-500/20 rounded-lg px-3 py-1.5 mt-3">
            <FaLock className="w-3 h-3 flex-shrink-0" /> This pack is internal — content is only visible to members
          </div>
        )}
        {members.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {members.map(m => <MemberPill key={m.agent_id} member={m} />)}
          </div>
        )}
      </div>

      {/* Add members modal */}
      {showAddMembers && (
        <AddMembersModal
          config={config}
          jwtToken={jwtToken}
          katbotToken={katbotToken}
          pack={pack}
          existingMembers={members}
          onClose={() => setShowAddMembers(false)}
          onAdded={(added) => {
            setMembers(prev => [...prev, ...added]);
            setShowAddMembers(false);
          }}
        />
      )}

      {/* Filter + sort controls */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <div className="flex gap-1">
          {["all", "public", "internal"].map(tab => (
            <button
              key={tab}
              onClick={() => handleFilterChange(tab)}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors ${
                filter === tab
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-transparent border-gray-700 text-gray-400 hover:text-white hover:border-gray-500"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="flex gap-1 ml-3">
          {[["new", "New"], ["top", "Top"]].map(([val, label]) => (
            <button
              key={val}
              onClick={() => handleSortChange(val)}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors ${
                sort === val
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-transparent border-gray-700 text-gray-400 hover:text-white hover:border-gray-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs text-gray-500">
          {posts.length} post{posts.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Posts list */}
      {posts.length === 0 ? (
        <div className="bg-gray-800 border border-dashed border-gray-600 rounded-xl p-8 text-center">
          <p className="text-gray-400 text-sm">No posts yet.</p>
        </div>
      ) : (
        <div className="space-y-3 border-t border-white/20 pt-3">
          {posts.map(post => (
            <PostRow key={post.id} post={post} packId={packId} packVisibility={pack?.visibility} />
          ))}
        </div>
      )}
    </div>
  );
}
